import React, { FC, useEffect, useMemo, useState } from "react";
import { CollapsePanelProps } from "rc-collapse/lib/interface";
import { SupplementsEndpoint } from "../../../store/endpoints/supplements";
import { useStore } from "effector-react";
import { $selectedLocation } from "../../../store/selected/selectedLocations";
import {
  $instancesIds,
  $supplementsServingVariants,
} from "../../../store/selected/shared";
import { SupplementCard } from "../../../cards";
import { Supplement } from "../../../store/models";
import {
  $selectedSupplements,
  SelectedSupplement,
  selectedSupplementsApi,
} from "../../../store/selected/selectedSupplements";
import {
  Grid,
  Pagination,
  Carousel,
  Collapse,
  Input,
  Alert,
  Skeleton,
} from "../../../components";
import { PagingMeta } from "../../../store/models/_system";
import { useDebounce, usePageSize } from "../../../hooks";
import { isPresent } from "../../../utils";
import { FiSearch } from "react-icons/fi";
import { AppConfig } from "../../../config";
import { useTranslation } from "react-i18next";

interface Props {
  data: { id: number; name: string };
  refreshCollapseHeight: () => void;
  search: string;
}

const PER_PAGE = 4;

type SupplementsDisplayData = (Supplement & {
  selectedData?: SelectedSupplement;
})[];

const SupplementGroup: FC<Props> = ({
  data,
  refreshCollapseHeight,
  search,
}) => {
  const { t } = useTranslation();
  const pageSize = usePageSize();

  const [supplements, setSupplements] = useState<Supplement[] | null>(null);
  const [pending, setPending] = useState<boolean>(false);

  const selectedLocation = useStore($selectedLocation);
  const selectedSupplements = useStore($selectedSupplements);
  const instancesIds = useStore($instancesIds);
  const supplementsServingVariants = useStore($supplementsServingVariants);

  const [meta, setMeta] = useState<PagingMeta | null>(null);

  const [viewMode, setViewMode] = useState<"pages" | "carousel">(
    pageSize.md ? "pages" : "carousel"
  );

  useEffect(() => {
    setViewMode(pageSize.md ? "pages" : "carousel");
  }, [pageSize.md]);

  useEffect(() => {
    read(false, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pageSize.md]);

  const read = async (isLoadMore?: boolean, specificPage?: number) => {
    if (selectedLocation) {
      if (isLoadMore && !meta?.nextPage) return;

      let page = 1;
      if (specificPage) {
        page = specificPage;
      } else if (isLoadMore && meta?.nextPage) {
        page = meta?.nextPage;
      }

      setPending(true);
      SupplementsEndpoint.list
        .call({
          _query: {
            location_id: selectedLocation?.id,
            category_id: data.id,
            page: page,
            per_page: PER_PAGE,
            search: search,
          },
          instances: instancesIds,
        })
        .then(({ data, meta }) => {
          setMeta(meta);
          setSupplements((existingData) => {
            if (isLoadMore) {
              return [...(existingData ?? []), ...data];
            }
            return data;
          });
        })
        .finally(() => {
          setPending(false);
          setTimeout(() => {
            refreshCollapseHeight();
          }, 100);
        });
    }
  };

  useEffect(() => {
    return () => {
      SupplementsEndpoint.list.cancel();
      SupplementsEndpoint.list.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingData = useMemo(() => {
    return new Array(PER_PAGE).fill(null);
  }, []);

  const displayData = useMemo<SupplementsDisplayData>(() => {
    if (!supplements) return [];

    return supplements.map((supplement) => {
      const selectedData = selectedSupplements.find(
        ({ entity: { id } }) => id === supplement.id
      );
      return {
        ...supplement,
        selectedData: selectedData,
      };
    });
  }, [supplements, selectedSupplements]);

  return (
    <>
      {viewMode === "pages" && (
        <>
          {displayData.map((supplement) => {
            return (
              <SupplementCard
                key={supplement.name + supplement.id}
                data={supplement}
                selectedData={supplement.selectedData}
                servingVariants={supplementsServingVariants}
                onQuantityChange={(v) => {
                  selectedSupplementsApi.setQuantity({
                    entity: supplement,
                    quantity: v ?? 0,
                  });
                }}
                onServingPlaceChange={(v) => {
                  selectedSupplementsApi.setServing({
                    entity: supplement,
                    activityId: v.activityId,
                    packageId: v.packageId,
                  });
                }}
              />
            );
          })}
          {pending &&
            pendingData.map((_, idx) => <SupplementCard.Skeleton key={idx} />)}

          {search && displayData.length === 0 && !pending && (
            <Alert variant={"info"}>Nothing found</Alert>
          )}
        </>
      )}

      {viewMode === "pages" && meta && meta.total > PER_PAGE && (
        <Grid container style={{ justifyContent: "center", marginTop: "2em" }}>
          <Grid item>
            <Pagination
              showLessItems
              total={meta.total}
              current={meta.current_page}
              pageSize={meta.per_page}
              onChange={(page) => read(false, page)}
            />
          </Grid>
        </Grid>
      )}

      {viewMode === "carousel" && (
        <Carousel
          carouselProps={{
            canLoadMore: isPresent(meta?.nextPage),
            selectedQuantity: selectedSupplements.length,
            onSlideChange: (e) => {
              if (e.isNextSlideDisabled && !e.isPrevSlideDisabled) {
                read(true);
              }
            },
            perPageLoad: meta?.per_page ?? 3,
          }}
        >
          {displayData.map((supplement) => {
            return (
              <SupplementCard
                key={supplement.name + supplement.id}
                data={supplement}
                selectedData={supplement.selectedData}
                servingVariants={supplementsServingVariants}
                onQuantityChange={(v) => {
                  selectedSupplementsApi.setQuantity({
                    entity: supplement,
                    quantity: v ?? 0,
                  });
                }}
                onServingPlaceChange={(v) => {
                  selectedSupplementsApi.setServing({
                    entity: supplement,
                    activityId: v.activityId,
                    packageId: v.packageId,
                  });
                }}
              />
            );
          })}

          {meta?.nextPage && (
            <Skeleton
              dir={"alternate"}
              height={450}
              style={{ marginLeft: 10, marginRight: 10 }}
            >
              {t(
                `${AppConfig.translationFileNames.base}:productsCarousel.swipeToLoadMore`
              )}
            </Skeleton>
          )}

          {search && displayData.length === 0 && !pending && (
            <Alert variant={"info"}>Nothing found</Alert>
          )}
        </Carousel>
      )}
    </>
  );
};

const SupplementGroupWrapper: FC<
  CollapsePanelProps & Omit<Props, "search">
> = ({ data, refreshCollapseHeight, ...collapseProps }) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useDebounce(searchValue, 1000);

  return (
    <Collapse.Panel
      {...collapseProps}
      header={
        <Grid container gutter={15} style={{ flexWrap: "nowrap" }}>
          <Grid item>
            <h3>{collapseProps.header}</h3>
          </Grid>
          {collapseProps.isActive && (
            <Grid item>
              <Input
                onClick={(e) => e.stopPropagation()}
                prepend={<FiSearch />}
                style={{ maxWidth: "200px" }}
                variant={"search"}
                type={"search"}
                size={"small"}
                value={searchValue}
                onChange={setSearchValue}
              />
            </Grid>
          )}
        </Grid>
      }
    >
      <SupplementGroup
        data={data}
        refreshCollapseHeight={refreshCollapseHeight}
        search={debouncedSearchValue}
      />
    </Collapse.Panel>
  );
};

export default SupplementGroupWrapper;
